import { HttpClient, HttpErrorResponse, HttpHeaderResponse, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { HubConnection, HubConnectionBuilder } from "@aspnet/signalr";
import { Subject, of, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { AuthTokenService } from './auth-token.service';
import { ApiService } from './api.service';
import { NotificationService } from "./notification.service";
import { HelperFunctionsService } from "./helperFunctions.service";
import { environment } from '@env/environment';

type ErrorMessage = {
  error: {
    [key: string]: { title: string; description: string; }
  }
}

@Injectable()
export class BatchApiService {
  private baseURL = environment.BATCH_IKYC_BASE_URL;
  private apiURL = `${this.baseURL}/api/`;
  private hubURL = `${this.baseURL}/hub/`;

  private hubConnection: HubConnection;

  private personSearchSubject = new Subject<any>();
  personSearchUpdate$ = this.personSearchSubject.asObservable();

  private businessSearchSubject = new Subject<any>();
  businessSearchUpdate$ = this.businessSearchSubject.asObservable();

  constructor(private httpClient: HttpClient, private apiService: ApiService,
    private authToken: AuthTokenService, private notificationService: NotificationService,
    private helperFunctionsService: HelperFunctionsService) {
    this.initSignalR();
  }

  private initSignalR() {
    this.hubConnection = new HubConnectionBuilder()
      .withUrl(this.hubURL + "BatchSearch")
      .build();

    this.hubConnection.on('ReceivePersonSearch', personSearch => {
      this.personSearchSubject.next(personSearch);
    })

    this.hubConnection.on('ReceiveBusinessSearch', businessSearch => {
      this.businessSearchSubject.next(businessSearch);
    })

    this.hubConnection.start().catch(error => {
      this.displayNotificationErrorAlert('Error starting SignalR Connection');
      this.displayNotificationErrorAlert(error);
    });
  }

  public fetch(url) {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.authToken.token}`
      })
    };

    return this.httpClient.get(this.apiURL + url, httpOptions).pipe(
      map((data: any) => data && data.data != null ? data.data : data),
      catchError(this.handleError)
    );
  }

  public post(url, body) {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.authToken.token}`,
        'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
      })
    };

    return this.httpClient.post(this.apiURL + url, body, httpOptions).pipe(
      map((data: any) => data && data.data != null ? data.data : data),
      catchError(this.handleError)
    )
  }

  public delete(url, body) {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.authToken.token}`
      }),
      body: body
    };

    return this.httpClient.delete(this.apiURL + url, httpOptions).pipe(
      map((data: any) => data && data.data != null ? data.data : data),
      catchError(this.handleError)
    )
  }

  private handleError(error: any) {
    // In a real world app, we might use a remote logging infrastructure
    // We'd also dig deeper into the error to get a better message
    // let errMsg = (error.error) ? error.error.message :
    //   error.status ? `${error.status} - ${error.statusText}` : 'Server error';
    console.error(error); // log to console instead
    return throwError(error);
    //return Observable.throw(errMsg);
  }

  /************ SERVICES *************/

  public displayNotificationInfoAlert(successTitle: string, timeout = 15000) {
    this.notificationService.smallBox({
      title: successTitle,
      content: "<i class='fa fa-clock-o'></i> <i>2 seconds ago...</i>",
      color: "#5bc0de",
      iconSmall: "fa fa-info-circle bounce animated",
      timeout: timeout
    });
  }

  public displayNotificationSuccessAlert(successTitle: string) {
    this.helperFunctionsService.displayNotificationSuccessAlert(successTitle);
  }

  public displayNotificationErrorAlert(error: any, timeout = 15000) {
    error = BatchApiService.getMappedError(error);

    if (error) {
      for (var key in error.error) {
        this.notificationService.smallBox({
          title: error.error[key].title,
          content: error.error[key].description + "<br/>",
          color: "#F08080",
          iconSmall: "fa fa-thumbs-down bounce animated",
          timeout: timeout
        });
      }
    }
  }

  private screeningDecisionValues: string[];
  public getScreeningDecisionValues() {
    if (this.screeningDecisionValues)
      return of(this.screeningDecisionValues);

    return this.fetch('BatchSearch/ScreeningDecisions')
      .pipe(tap(r => {
        if (Array.isArray(r)) {
          if (!r.includes(BatchApiService.MatchSreeningDecisionValue)) {
            throw new Error(`ScreeningDecisionValues does not include value for \"
            ${BatchApiService.MatchSreeningDecisionValue}\"`);
          }

          if (!r.includes(BatchApiService.NoMatchSreeningDecisionValue)) {
            throw new Error(`ScreeningDecisionValues does not include value for \"
            ${BatchApiService.NoMatchSreeningDecisionValue}\"`);
          }

          if (!r.includes(BatchApiService.PendingActionSreeningDecisionValue)) {
            throw new Error(`ScreeningDecisionValues does not include value for \"
            ${BatchApiService.PendingActionSreeningDecisionValue}\"`);
          }

          this.screeningDecisionValues = r;
        }
      }));
  }

  public getPersonTemplateSheetUrl() {
    return this.apiURL + 'BatchSearch/template/person';
  }

  public getBusinessTemplateSheetUrl() {
    return this.apiURL + 'BatchSearch/template/business';
  }

  public getPersonSearchResults() {
    return this.fetch('BatchSearch/person/1/99999');
  }

  public getBusinessSearchResults() {
    return this.fetch('BatchSearch/business/1/99999');
  }

  public getPersonSearchResultDetails(searchId: number) {
    return this.fetch(`BatchSearch/results/person/${searchId}`);
  }

  public getBusinessSearchResultDetails(searchId: number) {
    return this.fetch(`BatchSearch/results/business/${searchId}`);
  }

  public getPersonSearchResultsCount() {
    return this.fetch('BatchSearch/count/person');
  }

  public getBusinessSearchResultsCount() {
    return this.fetch('BatchSearch/count/business');
  }

  public uploadPersonBatchSheet(file: FormData) {
    return this.post('BatchSearch/upload/person', file);
  }

  public uploadBusinessBatchSheet(file: FormData) {
    return this.post('BatchSearch/upload/business', file);
  }

  public startBatchPersonScreening(ids: number[]) {
    return this.post('BatchSearch/search/person', ids);
  }

  public startBatchBusinessScreening(ids: number[]) {
    return this.post('BatchSearch/search/business', ids);
  }

  public setPersonScreeningDecision(searchId: number, matchId: number, status: string, notes: string) {
    let body = {
      searchId: searchId,
      matchId: matchId,
      status: status,
      notes: notes,
    };

    return this.post('BatchSearch/decision/person', body);
  }

  public setBusinessScreeningDecision(searchId: number, matchId: number, status: string, notes: string) {
    let body = {
      searchId: searchId,
      matchId: matchId,
      status: status,
      notes: notes,
    };

    return this.post('BatchSearch/decision/business', body);
  }

  public setNoMatchPersonScreeningDecision(searchId: number, matchIds: number[]) {
    let body = {
      searchId: searchId,
      matchIds: matchIds
    };

    return this.post('BatchSearch/nomatch/person', body);
  }

  public setNoMatchBusinessScreeningDecision(searchId: number, matchIds: number[]) {
    let body = {
      searchId: searchId,
      matchIds: matchIds
    };

    return this.post('BatchSearch/nomatch/business', body);
  }

  public deleteBatchPersons(ids: number[]) {
    return this.delete('BatchSearch/person', ids);
  }

  public deleteBatchBusinesses(ids: number[]) {
    return this.delete('BatchSearch/business', ids);
  }

  private currentSearchData;
  public setCurrentSearchData(searchData) {
    if (searchData != null)
      this.currentSearchData = {
        // Person Search Data
        fullName: searchData.fullName,
        dateOfBirth: searchData.dateOfBirth,

        // Business Search Data
        businessName: searchData.businessName,

        // Common Data
        referenceCode: searchData.referenceCode,
        address: searchData.address,
        city: searchData.city,
        postCode: searchData.postCode,
        country: searchData.country,
        threshold: searchData.threshold
      };
  }
  public getCurrentSearchData() {
    return this.currentSearchData;
  }

  /********** STATIC CLASSES **********/

  public static readonly MatchSreeningDecisionValue = 'Match'
  public static readonly NoMatchSreeningDecisionValue = 'NoMatch'
  public static readonly PendingActionSreeningDecisionValue = 'PendingAction'

  public static getMappedError(error): ErrorMessage {
    let errMsg: ErrorMessage = {
      error: {}
    };
    if (error) {
      if (typeof error === 'string') {
        return {
          error: {
            "Error": {
              title: '',
              description: error
            }
          }
        };
      }

      if (error instanceof HttpErrorResponse) {
        if (error.status === 0) {
          return {
            error: {
              "Error": {
                title: 'Server Unavailable',
                description: `Cannot connect to the target server at ${error.url}`
              }
            }
          };
        }

        if (error.status !== 400) {
          return {
            error: {
              "Error": {
                title: error.statusText,
                description: error.message
              }
            }
          };
        }
      }

      for (const key in error.error) {
        var value = error.error[key];
        const valueText = Array.isArray(value) ? value.join('<br />') : JSON.stringify(value);

        errMsg.error[key] = { title: key, description: valueText };
      }
    }

    return errMsg;
  }

  public static getModelError(error: HttpHeaderResponse) {
    let errorMsg: string;

    if (error instanceof HttpErrorResponse && error.status === 400) {
      for (const key in error.error) {
        var value = error.error[key];
        const valueText = Array.isArray(value) ? value.join('\n') : JSON.stringify(value);

        errorMsg += key + '\n';
        errorMsg += valueText + '\n';
      }
    }

    return errorMsg;
  }

  public static splitCamelCase(input: string) {
    return input.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  public static adjustToUtcDate(input: string) {
    return input != null && !input.endsWith('Z') ? input + 'Z' : input;
  }

  public static getPersonSearchClientModel(serverModel, clientModel = null) {
    if (clientModel == null)
      clientModel = {};

    clientModel.id = serverModel.id;
    clientModel.dateOfImport = BatchApiService.adjustToUtcDate(serverModel.dateOfImport);
    clientModel.referenceCode = serverModel.referenceCode;
    clientModel.fullName = serverModel.fullName;
    clientModel.dateOfBirth = BatchApiService.adjustToUtcDate(serverModel.dateOfBirth);
    clientModel.country = serverModel.country;
    clientModel.dateOfSearch = BatchApiService.adjustToUtcDate(serverModel.dateOfSearch);
    clientModel.status = BatchApiService.splitCamelCase(serverModel.status);
    clientModel.statusClass = serverModel.status == 'Completed' ? 'custom-bg-color-green'
      : serverModel.status == 'Pending' ? 'custom-bg-color-orange' : 'custom-bg-color-red';
    clientModel.matches = serverModel.matches;
    clientModel.threshold = serverModel.threshold;
    clientModel.decision = serverModel.decision == 'None' ? '' : BatchApiService.splitCamelCase(serverModel.decision);
    clientModel.notes = serverModel.notes;
    clientModel.address = serverModel.address;
    clientModel.city = serverModel.city;
    clientModel.postCode = serverModel.postCode;
    clientModel.isPEP = serverModel.isPEP;
    clientModel.isSanctionsCurrent = serverModel.isSanctionsCurrent;
    clientModel.isLawEnforcement = serverModel.isLawEnforcement;
    clientModel.isFinancialregulator = serverModel.isFinancialregulator;
    clientModel.isDisqualifiedDirector = serverModel.isDisqualifiedDirector;
    clientModel.isInsolvent = serverModel.isInsolvent;
    clientModel.isAdverseMedia = serverModel.isAdverseMedia;
    clientModel.isPepCurrent = serverModel.isPepCurrent;
    clientModel.isPepFormer = serverModel.isPepFormer;
    clientModel.isPepLinked = serverModel.isPepLinked;
    clientModel.isSanctionsFormer = serverModel.isSanctionsFormer;
    clientModel.isInRegulatoryEnforcementLists = serverModel.isInRegulatoryEnforcementLists;
    clientModel.isProfileOfInterest = serverModel.isProfileOfInterest;
    clientModel.hasGamblingRiskIntelligenceReport = serverModel.hasGamblingRiskIntelligenceReport;

    return clientModel;
  }

  public static getBusinessSearchClientModel(serverModel, clientModel = null) {
    if (clientModel == null)
      clientModel = {};

    clientModel.id = serverModel.id;
    clientModel.dateOfImport = BatchApiService.adjustToUtcDate(serverModel.dateOfImport);
    clientModel.referenceCode = serverModel.referenceCode;
    clientModel.businessName = serverModel.businessName;
    clientModel.registeredCountry = serverModel.registeredCountry;
    clientModel.dateOfSearch = BatchApiService.adjustToUtcDate(serverModel.dateOfSearch);
    clientModel.status = BatchApiService.splitCamelCase(serverModel.status);
    clientModel.statusClass = serverModel.status == 'Completed' ? 'custom-bg-color-green'
      : serverModel.status == 'Pending' ? 'custom-bg-color-orange' : 'custom-bg-color-red';
    clientModel.matches = serverModel.matches;
    clientModel.threshold = serverModel.threshold;
    clientModel.decision = serverModel.decision == 'None' ? '' : BatchApiService.splitCamelCase(serverModel.decision);
    clientModel.notes = serverModel.notes;
    clientModel.isPEP = serverModel.isPEP;
    clientModel.isPepLinked = serverModel.isPepLinked;
    clientModel.isSanctionsCurrent = serverModel.isSanctionsCurrent;
    clientModel.isSanctionsFormer = serverModel.isSanctionsFormer;
    clientModel.isLawEnforcement = serverModel.isLawEnforcement;
    clientModel.isFinancialregulator = serverModel.isFinancialregulator;
    clientModel.isDisqualifiedDirector = serverModel.isDisqualifiedDirector;
    clientModel.isInsolvent = serverModel.isInsolvent;
    clientModel.isAdverseMedia = serverModel.isAdverseMedia;
    clientModel.isInRegulatoryEnforcementLists = serverModel.isInRegulatoryEnforcementLists;
    clientModel.isProfileOfInterest = serverModel.isProfileOfInterest;
    clientModel.isCurrentlyAStateOwnedEnterprise = serverModel.isCurrentlyAStateOwnedEnterprise;
    clientModel.isFormerlyAStateOwnedEnterprise = serverModel.isFormerlyAStateOwnedEnterprise;

    return clientModel;
  }
}
